import React, { createContext, useCallback, useEffect, useMemo, useRef } from 'react';

interface FoldEntry {
  outerEl: HTMLElement;
  currentHeight: number;
  targetHeight: number;
  pending: boolean;
  onSettled?: () => void;
}

interface RegisterFoldOptions {
  onSettled?: () => void;
}

type StepTransitionTarget = HTMLElement | number;

interface HeightCoordinatorValue {
  registerFold: (id: string, outerEl: HTMLElement, options?: RegisterFoldOptions) => () => void;
  requestHeightChange: (id: string, targetHeight: number) => void;
  requestStepTransition: (target: StepTransitionTarget) => void;
  reportLoading: (id: string, loading: boolean) => void;
}

const noop = () => {};

const HeightCoordinatorContext = createContext<HeightCoordinatorValue>({
  registerFold: () => noop,
  requestHeightChange: noop,
  requestStepTransition: noop,
  reportLoading: noop
});

const SAFETY_TIMEOUT_MS = 5000;
const DOCUMENT_POSITION_PRECEDING = 2;

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const getScrollContainer = (): HTMLElement => {
  const root = document.scrollingElement as HTMLElement | null;
  if (root && root.scrollHeight > root.clientHeight) return root;
  return document.body;
};

interface AnimationItem {
  entry: FoldEntry;
  startHeight: number;
  targetHeight: number;
}

interface AnimationState {
  startTime: number;
  items: AnimationItem[];
  scrollContainer: HTMLElement | null;
  scrollFrom: number;
  scrollTo: number;
  hasScroll: boolean;
}

interface HeightCoordinatorProviderProps extends React.PropsWithChildren {
  debounceMs?: number;
  animationMs?: number;
}

const HeightCoordinatorProvider: React.FC<HeightCoordinatorProviderProps> = ({
  debounceMs = 50,
  animationMs = 250,
  children
}) => {
  const foldsRef = useRef(new Map<string, FoldEntry>());
  const loadingSetRef = useRef(new Set<string>());
  const scrollTargetRef = useRef<StepTransitionTarget | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const rafIdRef = useRef<number | null>(null);
  const animationRef = useRef<AnimationState | null>(null);
  // Indirection refs let scheduleRun and runAnimation reference each other
  // without circular useCallback deps, while still capturing the latest
  // debounceMs/animationMs from props.
  const scheduleRunRef = useRef<() => void>(noop);
  const runAnimationRef = useRef<() => void>(noop);

  scheduleRunRef.current = () => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (loadingSetRef.current.size > 0) return;
      runAnimationRef.current();
    }, debounceMs);
  };

  runAnimationRef.current = () => {
    const items: AnimationItem[] = [];
    for (const entry of foldsRef.current.values()) {
      entry.pending = false;
      if (entry.currentHeight !== entry.targetHeight) {
        items.push({
          entry,
          startHeight: entry.currentHeight,
          targetHeight: entry.targetHeight
        });
      }
    }

    const scrollTarget = scrollTargetRef.current;
    // Keep scrollTargetRef set so that follow-up animation cycles within the
    // same step transition (e.g. content loaded after the initial fold open
    // and ResizeObserver fired a new target) continue scrolling toward the
    // target instead of cancelling the in-flight scroll. Cleared on completion.

    if (items.length === 0 && scrollTarget === null) return;

    let scrollContainer: HTMLElement | null = null;
    let scrollFrom = 0;
    let scrollTo = 0;
    let hasScroll = false;

    if (scrollTarget !== null) {
      scrollContainer = getScrollContainer();
      scrollFrom = scrollContainer.scrollTop;

      if (typeof scrollTarget === 'number') {
        scrollTo = Math.max(0, scrollTarget);
      } else {
        const header = document.querySelector('header');
        const headerHeight = header ? header.getBoundingClientRect().height : 0;

        let deltaAbove = 0;
        for (const item of items) {
          const relation = scrollTarget.compareDocumentPosition(item.entry.outerEl);
          if (relation & DOCUMENT_POSITION_PRECEDING) {
            deltaAbove += item.targetHeight - item.startHeight;
          }
        }

        const rect = scrollTarget.getBoundingClientRect();
        const finalViewportTop = rect.top + deltaAbove;
        scrollTo = Math.max(0, scrollFrom + finalViewportTop - headerHeight);
      }

      hasScroll = Math.abs(scrollTo - scrollFrom) > 1;
    }

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const state: AnimationState = {
      startTime: performance.now(),
      items,
      scrollContainer,
      scrollFrom,
      scrollTo,
      hasScroll
    };
    animationRef.current = state;

    // User-scroll cancellation removed for now: trackpad/touch inertia from a
    // scroll immediately before the click was likely firing wheel events and
    // cancelling the auto-scroll. Animations are short (≈250ms); accepting
    // that user scroll during the animation will fight it briefly is fine.

    const tick = (now: number): void => {
      const anim = animationRef.current;
      if (!anim) return;
      const elapsed = now - anim.startTime;
      const t = animationMs <= 0 ? 1 : Math.min(1, elapsed / animationMs);
      const eased = easeInOutCubic(t);

      for (const item of anim.items) {
        const h = item.startHeight + (item.targetHeight - item.startHeight) * eased;
        item.entry.currentHeight = h;
        item.entry.outerEl.style.height = `${h}px`;
      }

      if (anim.hasScroll && anim.scrollContainer) {
        const y = anim.scrollFrom + (anim.scrollTo - anim.scrollFrom) * eased;
        anim.scrollContainer.scrollTop = y;
      }

      if (t < 1) {
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      for (const item of anim.items) {
        item.entry.currentHeight = item.targetHeight;
        item.entry.outerEl.style.height = `${item.targetHeight}px`;
      }
      rafIdRef.current = null;
      animationRef.current = null;
      for (const item of anim.items) {
        item.entry.onSettled?.();
      }
      let hasPending = false;
      for (const entry of foldsRef.current.values()) {
        if (entry.pending) {
          hasPending = true;
          break;
        }
      }
      if (hasPending) {
        // Carry the scroll target into the follow-up run so a debounced
        // ResizeObserver update (e.g. content settling after the initial
        // transition animation) continues toward the same target.
        scheduleRunRef.current();
      } else {
        scrollTargetRef.current = null;
      }
    };
    rafIdRef.current = requestAnimationFrame(tick);
  };

  const registerFold = useCallback<HeightCoordinatorValue['registerFold']>(
    (id, outerEl, options) => {
      const entry: FoldEntry = {
        outerEl,
        currentHeight: 0,
        targetHeight: 0,
        pending: false,
        onSettled: options?.onSettled
      };
      foldsRef.current.set(id, entry);
      outerEl.style.overflow = 'hidden';
      outerEl.style.height = '0px';
      return () => {
        foldsRef.current.delete(id);
      };
    },
    []
  );

  const requestHeightChange = useCallback<HeightCoordinatorValue['requestHeightChange']>(
    (id, targetHeight) => {
      const entry = foldsRef.current.get(id);
      if (!entry) return;
      if (entry.targetHeight === targetHeight) return;
      entry.targetHeight = targetHeight;
      entry.pending = true;
      scheduleRunRef.current();
    },
    []
  );

  const requestStepTransition = useCallback<HeightCoordinatorValue['requestStepTransition']>(
    target => {
      scrollTargetRef.current = target;
      scheduleRunRef.current();
    },
    []
  );

  const reportLoading = useCallback<HeightCoordinatorValue['reportLoading']>((id, loading) => {
    if (loading) {
      const wasEmpty = loadingSetRef.current.size === 0;
      loadingSetRef.current.add(id);
      clearTimeout(debounceTimerRef.current);
      if (wasEmpty) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = setTimeout(() => {
          loadingSetRef.current.clear();
          scheduleRunRef.current();
        }, SAFETY_TIMEOUT_MS);
      }
    } else {
      loadingSetRef.current.delete(id);
      if (loadingSetRef.current.size === 0) {
        clearTimeout(safetyTimerRef.current);
        scheduleRunRef.current();
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
      clearTimeout(safetyTimerRef.current);
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  const value = useMemo(
    () => ({ registerFold, requestHeightChange, requestStepTransition, reportLoading }),
    [registerFold, requestHeightChange, requestStepTransition, reportLoading]
  );

  return (
    <HeightCoordinatorContext.Provider value={value}>{children}</HeightCoordinatorContext.Provider>
  );
};

export default HeightCoordinatorContext;
export { HeightCoordinatorProvider };
